import React from "react";

interface EmptyStateAction {
  type: string;
  label: string;
}

function isEmptyStateAction(value: unknown): value is EmptyStateAction {
  if (!value || typeof value !== "object") return false;
  const obj = value as Record<string, unknown>;
  return typeof obj.type === "string" && typeof obj.label === "string";
}

interface EmptyStateBlockProps {
  payload: Record<string, unknown>;
  onAction?: (action: EmptyStateAction) => void;
}

/**
 * Themed empty surface for canvas blocks. Payload shape mirrors the
 * workspace render-spec compiler:
 *   { title, body?, action? }
 *
 * Visual spec: see .empty-state / .empty-state-title / .empty-state-desc
 * in sandbox-styles.css. Centered column with optional CTA below the
 * description.
 *
 * Distinct from `hank/src/ui/workspace/chat/EmptyState.tsx` which is the
 * full-page chat empty state.
 */
export default function EmptyStateBlock({ payload, onAction }: EmptyStateBlockProps) {
  const title = typeof payload.title === "string" ? payload.title : "";
  const body = typeof payload.body === "string" ? payload.body : undefined;
  const action = isEmptyStateAction(payload.action) ? payload.action : undefined;

  if (!title) {
    return (
      <div
        aria-label="empty-state-malformed"
        className="text-xs italic"
        style={{ color: "var(--c-text-faint)" }}
      >
        Empty state has no title.
      </div>
    );
  }

  return (
    <div
      aria-label="empty-state"
      className="flex flex-col items-center justify-center text-center"
      style={{ padding: "40px 16px" }}
    >
      <div
        className="font-medium"
        style={{ fontSize: 14, color: "var(--c-text)", marginBottom: 4 }}
      >
        {title}
      </div>
      {body && (
        <div
          className="text-xs"
          style={{
            color: "var(--c-text-faint)",
            maxWidth: 280,
            marginBottom: action ? 16 : 0,
          }}
        >
          {body}
        </div>
      )}
      {action && (
        <button
          type="button"
          data-agent-action-type={action.type}
          onClick={onAction ? () => onAction(action) : undefined}
          className="font-medium"
          style={{
            padding: "8px 14px",
            borderRadius: 6,
            fontSize: 13,
            backgroundColor: "var(--c-primary)",
            color: "var(--c-primary-text)",
            border: "1px solid var(--c-primary)",
            cursor: onAction ? "pointer" : "default",
          }}
        >
          {action.label}
        </button>
      )}
    </div>
  );
}
